<template>
    <div class="booking-form__container">
        <div v-if="formState == 'sending'">
            <div class="overlay"></div>
            <div class="form-sending"></div>
        </div>
        <div v-if="formState == 'sent'">
            <section class="section">
                <div class="container">
                    <div class="container content_call_to_action_container">
                        <div class="row align-items-center">
                            <div class="col-md-8">
                                <h3 class="content_call_to_action_container_h3">
                                    Kiitos tarjouspyynnöstäsi!
                                </h3>
                                <p class="content_call_to_action_container_p">
                                    {{ bandName }} vastaa tarjouspyyntöösi mahdollisimman pian, yleensä vuorokauden kuluessa.
                                    Mikäli haluat, voit pyytää tarjouksia myös muilta bändeiltä.
                                </p>
                            </div>
                            <div class="col-md-4">
                                <a href="/" class="btn btn--call-to-action">
                                    Siirry bändihakuun
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        <div class="booking-form" v-if="formVisible">
            <h2>Pyydä tarjous artistilta {{ bandName }}</h2>
            <p class="inline-hint">
                Tarjouspyyntö ei velvoita sinua tilaamaan artistia. Artisti lähettää sinulle tarjouksen mahdollisimman pian
                saatuaan tarjouspyyntösi.
            </p>
            <form v-on:submit.prevent="sendQuote" method="post" name="send-quote" class="default">
                <div class="row">
                    <div class="col-md-5">
                        <div class="booking-form__basic-info">
                            <div class="form-group" :class="{ 'form-group--error': $v.name.$error }">
                                <label>Nimesi</label>
                                <input type="text" placeholder="Nimesi" v-model.trim="$v.name.$model"/>
                            </div>

                            <div class="form-group" :class="{ 'form-group--error': $v.phone.$error }">
                                <label>Puhelinnumerosi</label>
                                <input type="text" placeholder="Puhelinnumerosi" v-model="$v.phone.$model"/>
                            </div>

                            <div class="form-group" :class="{ 'form-group--error': $v.email.$error }">
                                <label>Sähköpostiosoitteesi</label>
                                <input type="text" placeholder="Sähköpostiosoitteesi" v-model.lazy="$v.email.$model"/>
                            </div>

                            <div class="form-group" :class="{ 'form-group--error': $v.venue.$error }">
                                <label>Tapahtumapaikka / rafla</label>
                                <input type="text" placeholder="Tapahtumapaikka / rafla" v-model="$v.venue.$model"/>
                            </div>
                        </div>

                        <div class="booking-form__gig-info">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group" :class="{ 'form-group--error': $v.selectedDate.$error }">
                                        <label>Tapahtuman päivämäärä</label>
                                        <datepicker
                                            :value="selectedDate"
                                            :disabledDates="datepickerSelections.disabledDates"
                                            placeholder="Tapahtuman päivämäärä"
                                            format="dd.MM.yyyy"
                                            :monday-first="true"
                                            @selected="onDatepickerSelect">
                                        </datepicker>
                                    </div>
                                </div>
                                <div class="col-md-5 offset-md-1">
                                    <city-field
                                        :city-options=cityOptions
                                        v-model="selectedCity">
                                    </city-field>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <crowd-field
                                        :crowd-options=crowdOptions
                                        v-model="selectedCrowd">
                                    </crowd-field>
                                </div>
                                <div class="col-md-5 offset-md-1">
                                    <audience-field-dropdown
                                        :audience-options=audienceOptions
                                        v-model="selectedAudience">
                                    </audience-field-dropdown>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-5 offset-md-1">
                        <div class="row booking-form__message">
                            <div class="col-md-12 text-center">
                                <textarea
                                    rows="12"
                                    v-model="message"
                                    @focusin="removeMessagePlaceholder"
                                    @focusout="addMessagePlaceholder"
                                ></textarea>

                                <button type="submit" class="btn btn--call-to-action">
                                    Lähetä tarjouspyyntö
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import Datepicker from 'vuejs-datepicker';
    import required from 'vuelidate/lib/validators/required';
    import email from 'vuelidate/lib/validators/email';
    import CityField from 'components/CityField';
    import CrowdField from 'components/CrowdField';
    import AudienceFieldDropdown from 'components/AudienceFieldDropDown';
    import { cannotBeInPast, mustBeInFuture } from '../modules/Validators';
    import ApiCall from '../modules/ApiCall';
    import Routes from '../modules/Routes';
    import FormData from '../modules/FormData';
    import Messages from '../modules/Messages';

    export default {
        components: {
            Datepicker,
            CityField,
            CrowdField,
            AudienceFieldDropdown
        },
        mounted() {
            ApiCall.getCached(Routes.generate('_public_api_options_base_data'), true).then((formData) => {
                this.cityOptions = formData.cities;
                this.audienceOptions = formData.audiences;
                this.crowdOptions = formData.crowds;
            });
        },
        props: {
            heading: {
                type: String,
                default: 'Buukkaa bändi'
            },
            showHeading: {
                type: Boolean,
                default: true
            },
            bandName: {
                type: String
            },
            bandId: {
                type: String
            },
            initialDate: {
                type: String
            },
            initialCity: {
                type: Object
            },
            initialAudience: {
                type: Object
            },
            initialCrowd: {
                type: Object
            },
            bandSlug: {
                type: String
            },
            initialMessage: {
                type: String,
                default: 'Kirjoita tähän lisätietoja bändille tapahtumapaikasta, soittoajasta, yms.'
            }
        },
        data() {
            return {
                selectedCity: this.initialCity,
                selectedCrowd: this.initialCrowd,
                selectedAudience: this.initialAudience,
                selectedDate: moment(this.initialDate).toDate(),
                cityOptions: [],
                crowdOptions: [],
                audienceOptions: [],
                message: this.initialMessage,
                name: '',
                phone: '',
                email: '',
                venue: '',
                formVisible: true,
                formState: 'not-sent',
                datepickerSelections: {
                    disabledDates: {
                        to: moment().add(2, 'days').toDate()
                    }
                }
            };
        },
        validations: {
            selectedCity: {
                required
            },
            name: {
                required
            },
            phone: {
                required
            },
            email: {
                required,
                email
            },
            venue: {
                required
            },
            selectedDate: {
                cannotBeInPast,
                mustBeInFuture: mustBeInFuture(2)
            }
        },
        methods: {
            getUrl() {
                return Routes.generate('_public_api_send_quote', { bandSlug: this.bandSlug });
            },
            sendQuote() {
                this.$v.$touch();

                if (this.$v.$invalid) {
                    Messages.error('Täytäthän kaikki lomakkeen tiedot');
                    return;
                }
                this.formState = 'sending';
                let formData = new FormData(this.$data);
                this.$http.post(this.getUrl(), JSON.stringify(formData.parseBookingData()), {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }).then((response) => {
                    Messages.fromResponse(response);
                    this.formState = 'sent';
                    this.formVisible = false;
                    this.$ga.event('Quote', 'Sent successfully');
                    gtag('event', 'send_quote');
                }, (response) => {
                    Messages.fromResponse(response);
                    this.formState = 'error';
                    this.$ga.event('Quote', 'Sending failed');
                });
            },
            showForm() {
                this.formVisible = true;
            },
            hideForm() {
                this.formVisible = false;
            },
            onDatepickerSelect(selectedDate) {
                this.selectedDate = selectedDate;
                this.$v.selectedDate.$touch();
            },
            removeMessagePlaceholder() {
                if (this.message === this.initialMessage) {
                    this.message = ' ';
                }
            },
            addMessagePlaceholder() {
                if (!this.message || /^\s*$/.test(this.message)) {
                    this.message = this.initialMessage;
                }
            }
        }
    };
</script>
